import { keepPreviousData } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { PiggyBank } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { RANGE_OPTIONS, type RangeOption } from '../../components/bills/cost-history';
import { EmptyState } from '../../components/common/EmptyState';
import { CardSkeleton } from '../../components/common/Skeleton';
import { SavingsPotCard } from '../../components/savings/SavingsPotCard';
import { SavingsTotalCard } from '../../components/savings/SavingsTotalCard';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

const searchSchema = z.object({
  months: z.coerce.number().int().optional().catch(undefined),
});

export const Route = createFileRoute('/savings/')({
  validateSearch: searchSchema,
  component: SavingsPage,
});

function toRange(months: number | undefined): RangeOption {
  return RANGE_OPTIONS.find((option) => option === months) ?? 12;
}

/** Sparande: what has actually been moved to savings, per pot and period. */
function SavingsPage() {
  usePageTitle('Sparande');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const range = toRange(search.months);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const query = trpc.savings.history.useQuery(
    { months: range },
    { retry: false, placeholderData: keepPreviousData },
  );

  const setRange = (months: RangeOption) =>
    void navigate({
      to: '/savings',
      search: { months: months === 12 ? undefined : months },
      replace: true,
    });

  const data = query.data;
  const pots = data?.pots ?? [];
  const anyMovement = pots.some((p) => p.totalDeposited > 0 || p.totalWithdrawn > 0);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Sparande</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Det ni faktiskt har flyttat till sparande, löneperiod för löneperiod.
        </p>
      </header>

      {query.error ? (
        <EmptyState
          icon="⚠️"
          title="Kunde inte hämta sparandet"
          description="Försök igen om en liten stund."
        />
      ) : !query.isLoading && pots.length === 0 ? (
        <EmptyState
          icon="🐷"
          title="Inga sparpottar än"
          description="En sparpott är en kategori i gruppen Sparande, till exempel Buffert eller Barnspar. Skapa en under Kategorier."
          action={{
            label: 'Gå till Kategorier',
            onClick: () => void navigate({ to: '/settings', search: { tab: 'categories' } }),
          }}
        />
      ) : (
        <>
          {!query.isLoading && !anyMovement && <HowTo />}

          <SavingsTotalCard
            data={data}
            isLoading={query.isLoading}
            range={range}
            onRangeChange={setRange}
          />

          <section aria-labelledby="savings-pots-heading" className="space-y-2">
            <div className="flex items-baseline justify-between gap-3 px-1">
              <h2 id="savings-pots-heading" className="text-[15px] font-semibold">
                Per sparpott
              </h2>
              <Link
                to="/settings"
                search={{ tab: 'categories' }}
                className="inline-flex min-h-11 items-center text-sm font-medium text-primary hover:underline sm:min-h-0"
              >
                Hantera pottar
              </Link>
            </div>
            {query.isLoading || !data ? (
              <div className="space-y-2">
                <CardSkeleton lines={2} />
                <CardSkeleton lines={2} />
              </div>
            ) : (
              <ul className="space-y-2">
                {pots.map((pot) => (
                  <SavingsPotCard
                    key={pot.categoryId}
                    pot={pot}
                    range={range}
                    expanded={expandedId === pot.categoryId}
                    onToggle={() =>
                      setExpandedId((current) =>
                        current === pot.categoryId ? null : pot.categoryId,
                      )
                    }
                  />
                ))}
              </ul>
            )}
          </section>

          {!query.isLoading && anyMovement && (
            <p className="px-1 text-xs text-muted-foreground">
              Har ni flyttat pengar till sparande? Öppna överföringen under{' '}
              <Link
                to="/transactions"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Köp
              </Link>{' '}
              och välj Sparande.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Empty state: how a transfer becomes savings */
function HowTo() {
  return (
    <section className="rounded-2xl border border-dashed border-border px-5 py-6">
      <span
        aria-hidden
        className="flex size-11 items-center justify-center rounded-full bg-positive-soft text-positive"
      >
        <PiggyBank className="size-5" />
      </span>
      <h2 className="mt-3 text-[15px] font-semibold">Inget sparat än</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Sparande är det ni faktiskt flyttar. Så här markerar ni det:
      </p>
      <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm">
        <li>Gör överföringen till sparkontot som vanligt.</li>
        <li>
          Öppna den under{' '}
          <Link to="/transactions" className="font-medium text-primary hover:underline">
            Köp
          </Link>{' '}
          eller i Att sortera.
        </li>
        <li>
          Tryck på <span className="font-medium">Sparande</span> och välj sparpott. Då räknas den
          som sparad i stället för som en överföring.
        </li>
      </ol>
    </section>
  );
}
